import React from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import { useQuery } from "@apollo/client/react";
import CircularProgress from "@mui/material/CircularProgress";
import { FETCH_PROJECT_QUERY } from "../../../../../constants/graphql/queries";
import { fullWidthDialog } from "../../../../../constants/dialog";
import useWizardStep from "../../../../../hooks/useWizardStep";
import Details from "./Details/Details";
import Items from "./Items/Items";
import AncillaryCharges from "./AncillaryCharges/AncillaryCharges";
import FinalReleasePhotos from "./FinalReleasePhotos/FinalReleasePhotos";
import Confirmation from "./Confirmation/Confirmation";
import IntegrationCounterpartStep from "src/components/integrations/IntegrationCounterpartStep";
import useHasEnabledIntegration from "src/hooks/useHasEnabledIntegration";
import { withIntegrationStep, wizardProgress } from "src/utils/integrationWizardSteps";
import { getWO } from "../../../utils";
import { useNavigate } from "react-router";
import useHasAdditionalCharges from "src/components/capture/ReceivingWizard/hooks/useHasAdditionalCharges";

const BASE_STEPS = ["details", "items", "charges", "photos", "confirmation"];

export default function OutboundOrderGuidedWizardModal({ inventoryRequest, onToggle }) {
  const navigate = useNavigate();
  const { data: { project = {} } = {}, loading: loadingProject } = useQuery(FETCH_PROJECT_QUERY, {
    variables: { id: inventoryRequest?.projectId },
    skip: !inventoryRequest?.projectId,
  });
  const { hasIntegration, loading: loadingIntegration } = useHasEnabledIntegration(project);
  const STEPS = withIntegrationStep(BASE_STEPS, hasIntegration);
  const hasAdditionalCharges = useHasAdditionalCharges(project, "outbound");

  const handleClose = () => {
    onToggle();
  };

  const loadingInventoryRequest = !inventoryRequest || !inventoryRequest.id;
  const loading = loadingProject || loadingInventoryRequest || loadingIntegration;
  const step = useWizardStep(STEPS, { skip: loading });

  // Prevent closing during critical steps (items, charges, photos)
  const criticalSteps = ["items", "charges", "photos"];
  const preventBackdropClose = criticalSteps.includes(step);

  return (
    <Dialog 
      open 
      maxWidth="sm" 
      fullWidth 
      onClose={preventBackdropClose ? null : handleClose} 
      sx={fullWidthDialog}
    >
      <Box display="flex" flexDirection="column" height="100%">
        {loading ? (
          <Box p={3} display="flex" minHeight={400} alignItems="center" justifyContent="center">
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box minHeight={400} height="100%">
            {step === "details" && (
              <Details project={project} inventoryRequest={inventoryRequest} />
            )}
            {step === "items" && <Items project={project} inventoryRequest={inventoryRequest} />}
            {step === "integration" && (
              <IntegrationCounterpartStep
                title="Link Moveware Diary Action"
                info={getWO(inventoryRequest)}
                project={project}
                linkableType="InventoryRequest"
                linkableId={inventoryRequest?.id}
                date={inventoryRequest?.deliveryDate}
                {...wizardProgress(STEPS, "integration")}
                startTime={inventoryRequest?.pickingStartTime}
                completeTime={inventoryRequest?.pickingCompleteTime}
                onBack={() => {
                  const params = new URLSearchParams(window.location.search);
                  params.set("step", "items");
                  navigate({ search: params.toString() });
                }}
                onContinue={() => {
                  const params = new URLSearchParams(window.location.search);
                  params.set("step", hasAdditionalCharges ? "charges" : "photos");
                  navigate({ search: params.toString() });
                }}
              />
            )}
            {step === "charges" && (
              <AncillaryCharges project={project} inventoryRequest={inventoryRequest} />
            )}
            {step === "photos" && (
              <FinalReleasePhotos project={project} inventoryRequest={inventoryRequest} />
            )}
            {step === "confirmation" && (
              <Confirmation project={project} inventoryRequest={inventoryRequest} onClose={handleClose} />
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
